import { AuthBaseClient } from '../base-client';
import type {
  IntegrationResponse,
  CreateIntegrationRequest,
  UpdateIntegrationRequest,
  SyncOptions,
  SyncResult,
  PatientSyncResult,
  CatalogImportObject,
  CatalogImportResult,
  TestCredentialsRequest,
  TestCredentialsResult,
  ToggleIntegrationRequest,
  OAuthAuthorizeResponse,
  EhrActivityResponse,
  IntegrationStreamName,
  IntegrationStreamsResponse,
  IntegrationStreamView,
  RunStreamsResult,
  ChangeFeedName,
  IntegrationSubscriptionsResponse,
  IntegrationSubscriptionView,
} from './types';

/**
 * Client for integration API endpoints.
 */
export class IntegrationClient extends AuthBaseClient {
  /**
   * Get all integrations for current organization.
   */
  async getAll(): Promise<IntegrationResponse[]> {
    return this.get<IntegrationResponse[]>('/api/integrations');
  }

  /**
   * Get integration by ID.
   */
  async getById(id: string): Promise<IntegrationResponse> {
    return this.get<IntegrationResponse>(`/api/integrations/${id}`);
  }

  /**
   * Create a new integration.
   */
  async create(data: CreateIntegrationRequest): Promise<IntegrationResponse> {
    return this.post<IntegrationResponse>('/api/integrations', data);
  }

  /**
   * Update an integration.
   */
  async update(id: string, data: UpdateIntegrationRequest): Promise<IntegrationResponse> {
    return this.patch<IntegrationResponse>(`/api/integrations/${id}`, data);
  }

  /**
   * Delete an integration.
   */
  async remove(id: string): Promise<null> {
    return this.delete<null>(`/api/integrations/${id}`);
  }

  /**
   * Enable or disable an integration.
   */
  async toggle(id: string, enabled: boolean): Promise<IntegrationResponse> {
    const body: ToggleIntegrationRequest = { enabled };
    return this.patch<IntegrationResponse>(`/api/integrations/${id}/toggle`, body);
  }

  /**
   * Trigger a sync for an integration.
   */
  async sync(id: string, options?: SyncOptions): Promise<SyncResult> {
    return this.post<SyncResult>(`/api/integrations/${id}/sync`, options ?? {});
  }

  /**
   * Sync a single patient by EHR patient ID.
   */
  async syncPatient(integrationId: string, ehrPatientId: string): Promise<PatientSyncResult> {
    return this.post<PatientSyncResult>(
      `/api/integrations/${integrationId}/sync/${ehrPatientId}`,
      {}
    );
  }

  /**
   * Pull selected catalog objects (providers, locations, appointment types,
   * available slots) from the EHR and upsert them by EHR id.
   */
  async importCatalog(id: string, objects: CatalogImportObject[]): Promise<CatalogImportResult> {
    return this.post<CatalogImportResult>(`/api/integrations/${id}/import`, { objects });
  }

  /**
   * Test credentials against the EHR connector without saving.
   */
  async testCredentials(data: TestCredentialsRequest): Promise<TestCredentialsResult> {
    return this.post<TestCredentialsResult>('/api/integrations/test', data);
  }

  /**
   * Get OAuth authorization URL for a given source (e.g., 'gmail').
   */
  async getOAuthUrl(source: string): Promise<OAuthAuthorizeResponse> {
    return this.get<OAuthAuthorizeResponse>(
      `/api/integrations/oauth/authorize?source=${encodeURIComponent(source)}`
    );
  }

  /**
   * Recent EHR API calls the org's connector has made (ADR-095) — the
   * connector-activity panel. `supported` is false when the org's EHR connector
   * can't report its calls; `source` is null when the org has no EHR integration.
   */
  async getEhrActivity(limit?: number): Promise<EhrActivityResponse> {
    const query = limit ? `?limit=${encodeURIComponent(String(limit))}` : '';
    return this.get<EhrActivityResponse>(`/api/integrations/ehr/activity${query}`);
  }

  /** The integration's syncs, each with its derived status (ADR-299). */
  async getStreams(id: string): Promise<IntegrationStreamsResponse> {
    return this.get<IntegrationStreamsResponse>(`/api/integrations/${id}/streams`);
  }

  /** Run the chosen syncs now; each answers whether it could start. */
  async runStreams(id: string, streams: IntegrationStreamName[]): Promise<RunStreamsResult> {
    return this.post<RunStreamsResult>(`/api/integrations/${id}/streams/run`, { streams });
  }

  /** Pause or resume one sync. */
  async setStreamEnabled(
    id: string,
    stream: IntegrationStreamName,
    enabled: boolean
  ): Promise<IntegrationStreamView> {
    return this.patch<IntegrationStreamView>(`/api/integrations/${id}/streams/${stream}`, {
      enabled,
    });
  }

  /** The EHR change-feed subscriptions; `check` re-reads them from the EHR first. */
  async getSubscriptions(id: string, check = false): Promise<IntegrationSubscriptionsResponse> {
    const query = check ? '?check=true' : '';
    return this.get<IntegrationSubscriptionsResponse>(
      `/api/integrations/${id}/subscriptions${query}`
    );
  }

  /** Say whether a feed should be subscribed; the API makes the EHR follow. */
  async setSubscription(
    id: string,
    feed: ChangeFeedName,
    desired: 'subscribed' | 'unsubscribed'
  ): Promise<IntegrationSubscriptionView> {
    return this.put<IntegrationSubscriptionView>(`/api/integrations/${id}/subscriptions/${feed}`, {
      desired,
    });
  }
}
